<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-2">
      <!-- Academic Year Filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.academic_year_id"
          @change="applyFilters"
        >
          <option value="" disabled>{{ t("filters.select_academic_year") }}</option>
          <option value="">{{ t("filters.all_academic_years") }}</option>
          <option 
            v-for="year in academicYears" 
            :key="year.id" 
            :value="year.id"
          >
            {{ year.name }} ({{ year.year }})
            <span v-if="year.is_active"> - {{ t("common.current") }}</span>
          </option>
        </select>
      </div>

      <!-- Class Filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.class_id"
          @change="handleClassChange"
        >
          <option value="" disabled>{{ t("filters.select_class") }}</option>
          <option value="">{{ t("filters.all_classes") }}</option>
          <option 
            v-for="classItem in filteredClasses" 
            :key="classItem.id" 
            :value="classItem.id"
          >
            {{ classItem.name }}
          </option>
        </select>
      </div>

      <!-- Subject Filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.subject_id"
          :disabled="!localFilters.class_id"
          @change="applyFilters"
        >
          <option value="" disabled>
            {{ localFilters.class_id ? t("filters.select_subject") : t("filters.select_class_first") }}
          </option>
          <option
            v-for="subject in filteredSubjects"
            :key="subject.id"
            :value="subject.id"
          >
            {{ subject.name }}
          </option>
        </select>
      </div>

      <!-- Reset Button (Desktop) -->
      <div class="col-md-1 d-none d-md-block">
        <button 
          class="btn btn-outline-secondary btn-sm w-100" 
          @click="resetFilters"
          :title="t('filters.reset_title')"
        >
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>  

      <!-- Search Input -->
       <div class="col-6 col-md-2 col-lg-2 position-relative">
        <input
          type="text"
          class="form-control form-control-sm ps-5"
          :placeholder="t('filters.search_placeholder')"
          v-model="localFilters.search"
          @input="handleSearchInput"
          style="padding-right: 12px"
        />
        <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
          <i class="fas fa-search"></i>
        </span>
      </div>

      <!-- Reset Button (Mobile) -->
      <div class="col-12 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ t("filters.reset") }}
        </button>
      </div>

      <!-- Add Button -->
      <div class="col-12 col-md-3 col-lg-3">
        <button 
          class="btn btn-secondary btn-sm w-100" 
          @click="$emit('add')"
          v-can="'grade_sheets.create'"
        >
          <i class="fas fa-plus me-2"></i>
          {{ t("filters.add") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, computed } from 'vue';
import { debounce } from 'lodash';
import { useToast } from 'vue-toastification';
import api from '@/api';
import { useStore } from 'vuex';
import Cookies from 'js-cookie';
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const toast = useToast();
const store = useStore();

const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      search: '',
      class_id: '',
      subject_id: '',
      teacher_id: '',
      academic_year_id: ''
    })
  },
  classes: {
    type: Array,
    default: () => []
  },
  teachers: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits([
  'update:filters', 
  'apply-filters', 
  'reset-filters', 
  'add',
  'search',
  'filter-by-class',
  'filter-by-subject',
  'filter-by-teacher',
  'filter-by-year'
]);

const academicYears = ref([]);
const localFilters = ref(JSON.parse(JSON.stringify(props.filters)));
const classSubjects = ref([]);
const teacherClasses = ref([]);
const teacherSubjects = ref([]);
const teachers = ref(props.teachers);

const currentUser = computed(() => {
  return store.state.user || {
    role: {
      slug: Cookies.get('user_type') || 'teacher'
    }
  };
});

const isTeacher = computed(() => {
  return currentUser.value?.role?.slug === 'teacher';
});

const filteredClasses = computed(() => {
  return isTeacher.value ? teacherClasses.value : props.classes;
});

const filteredSubjects = computed(() => {
  return isTeacher.value ? teacherSubjects.value : classSubjects.value;
});

onMounted(async () => {
  await loadAcademicYears();
  
  if (isTeacher.value) {
    await fetchTeacherClasses();
  }
  
  if (localFilters.value.class_id) {
    await fetchSubjects();
  }
});

const loadAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
    
    const currentYear = academicYears.value.find(year => year.is_active);
    if (currentYear && !localFilters.value.academic_year_id && !props.filters.academic_year_id) {
      localFilters.value.academic_year_id = currentYear.id;
    }
  } catch (error) {
    toast.error(t("filters.errors.fetch_academic_years"));
    academicYears.value = [];
  }
};

const fetchTeacherClasses = async () => {
  try {
    const response = await api.get(`/teachers/${currentUser.value.id}/classes`);
    teacherClasses.value = response.data.data;
    
    if (teacherClasses.value.length === 1 && !localFilters.value.class_id && !props.filters.class_id) {
      localFilters.value.class_id = teacherClasses.value[0].id;
      await fetchTeacherSubjects();
    }
  } catch (error) {
    toast.error(t("filters.errors.fetch_teacher_classes"));
    teacherClasses.value = [];
  }
};

const fetchTeacherSubjects = async () => {
  if (!localFilters.value.class_id) {
    teacherSubjects.value = [];
    return;
  }

  try {
    const response = await api.get(`/teachers/${currentUser.value.id}/subjects`, {
      params: { class_id: localFilters.value.class_id }
    });
    teacherSubjects.value = response.data.data;
    
    if (teacherSubjects.value.length === 1 && !localFilters.value.subject_id && !props.filters.subject_id) {
      localFilters.value.subject_id = teacherSubjects.value[0].id;
      localFilters.value.teacher_id = currentUser.value.id;
      applyFilters();
    }
  } catch (error) {
    toast.error(t("filters.errors.fetch_teacher_subjects"));
    teacherSubjects.value = [];
  }
};

const fetchSubjects = async () => {
  if (isTeacher.value) {
    await fetchTeacherSubjects();
  } else {
    await fetchClassSubjects();
  }
};

const fetchClassSubjects = async () => {
  if (!localFilters.value.class_id) {
    classSubjects.value = [];
    return;
  }

  try {
    const response = await api.get(`/classes/${localFilters.value.class_id}/subjects`);
    classSubjects.value = response.data.data.map(item => ({
      id: item.subject_id,
      name: item.subject?.name || t("filters.default_subject", { id: item.subject_id })
    }));
  } catch (error) {
    toast.error(t("filters.errors.fetch_class_subjects"));
    classSubjects.value = [];
  }
};

const handleClassChange = async () => {
  localFilters.value.subject_id = '';
  await fetchSubjects();
  applyFilters();
};

const debouncedSearch = debounce(() => {
  applyFilters();
}, 500);

const handleSearchInput = () => {
  debouncedSearch();
};

const applyFilters = () => {
  emit('update:filters', { ...localFilters.value });
  emit('apply-filters');
};

const resetFilters = () => {
  debouncedSearch.cancel(); 
  
  const currentYear = academicYears.value.find(year => year.is_active);
  
  localFilters.value = {
    search: '',
    class_id: '',
    subject_id: '',
    teacher_id: isTeacher.value ? currentUser.value.id : '',
    academic_year_id: currentYear ? currentYear.id : ''
  };
  
  if (isTeacher.value) {
    teacherSubjects.value = [];
  } else {
    classSubjects.value = [];
  }
  
  emit('update:filters', localFilters.value);
  emit('reset-filters');
};

watch(() => props.filters, (newVal) => {
  localFilters.value = {
    search: newVal.search || '',
    class_id: newVal.class_id || '',
    subject_id: newVal.subject_id || '',
    teacher_id: newVal.teacher_id || (isTeacher.value ? currentUser.value.id : ''),
    academic_year_id: newVal.academic_year_id || ''
  };
}, { deep: true, immediate: true });

watch(() => props.teachers, (newVal) => {
  teachers.value = newVal;
});
</script>